// The handset's own contact book — the phone book, not the portal directory.
//
// Everything here works in RAW contacts. A phone signed into three Google
// accounts holds the same person three times, once per account, and the
// contacts app shows them merged as one card. Deleting the card would take all
// three; deleting a raw contact takes exactly one account's copy, and that
// account syncs the deletion to Google.
const isNative = !!(window as any).Capacitor?.isNativePlatform?.();

export const isDeviceContactsAvailable = isNative;

export interface DeviceContact {
  rawId: number;
  contactId: string;
  accountName: string;
  accountType: string;
  name: string;
  numbers: string[];
}

export interface ContactAccount {
  name: string;
  type: string;
  count: number;
}

let lastError: string | null = null;
export const getLastContactsError = () => lastError;

// Boxed for the same reason as callLog.ts: registerPlugin hands back a Proxy
// that turns any property access into a native call, so letting it sit on an
// await path makes the runtime probe `.then` and blow up.
interface PluginBox { p: any }
let pluginPromise: Promise<PluginBox | null> | null = null;

function getPluginBox(): Promise<PluginBox | null> {
  if (!isNative) return Promise.resolve(null);
  if (!pluginPromise) {
    pluginPromise = (async () => {
      try {
        const { registerPlugin } = await import('@capacitor/core');
        return { p: registerPlugin('DeviceContacts') };
      } catch (e: any) {
        lastError = e?.message || String(e);
        return null;
      }
    })();
  }
  return pluginPromise;
}

export async function checkContactsPermission(): Promise<boolean> {
  const box = await getPluginBox();
  if (!box) return false;
  try {
    const res = await box.p.checkPermission();
    return !!res?.granted;
  } catch (e: any) {
    lastError = e?.message || String(e);
    return false;
  }
}

export async function requestContactsPermission(): Promise<boolean> {
  const box = await getPluginBox();
  if (!box) return false;
  try {
    const res = await box.p.requestPermission();
    return !!res?.granted;
  } catch (e: any) {
    lastError = e?.message || String(e);
    return false;
  }
}

/** The accounts the phone keeps contacts in, and how many each holds. */
export async function getContactAccounts(): Promise<ContactAccount[]> {
  const box = await getPluginBox();
  if (!box) return [];
  const res = await box.p.accounts();
  return (res?.accounts || []) as ContactAccount[];
}

/** Every raw contact carrying at least one phone number. */
export async function getDeviceContacts(): Promise<DeviceContact[]> {
  const box = await getPluginBox();
  if (!box) return [];
  const res = await box.p.list();
  return (res?.contacts || []) as DeviceContact[];
}

/**
 * Delete raw contacts by id, in batches.
 *
 * Batched because a phone book of this size means thousands of provider
 * deletes, and a single call that runs for a minute looks like a hung app.
 * onProgress lets the screen say how far it has got.
 */
export async function deleteDeviceContacts(
  rawIds: number[],
  onProgress?: (done: number, total: number) => void,
): Promise<{ deleted: number; failed: number[] }> {
  const box = await getPluginBox();
  if (!box) return { deleted: 0, failed: rawIds };
  const CHUNK = 200;
  let deleted = 0;
  const failed: number[] = [];
  for (let i = 0; i < rawIds.length; i += CHUNK) {
    const slice = rawIds.slice(i, i + CHUNK);
    const res = await box.p.deleteRawContacts({ ids: slice });
    deleted += Number(res?.deleted) || 0;
    (res?.failed || []).forEach((id: any) => failed.push(Number(id)));
    onProgress?.(Math.min(i + CHUNK, rawIds.length), rawIds.length);
  }
  return { deleted, failed };
}

/** The digits that identify a subscriber: the last ten for an Indian mobile. */
export function normalizeNumber(raw: string | null | undefined): string {
  const d = String(raw || '').replace(/\D/g, '');
  return d.length > 10 ? d.slice(-10) : d;
}

/** "google" / "com.google" → Google; anything else shown as it comes. */
export function accountLabel(c: { accountName?: string; accountType?: string }): string {
  const name = (c.accountName || '').trim();
  const type = (c.accountType || '').trim();
  if (!name && !type) return 'Phone only';
  if (/google/i.test(type)) return name || 'Google';
  if (/sim|icc/i.test(type)) return name ? `SIM (${name})` : 'SIM';
  if (/whatsapp/i.test(type)) return 'WhatsApp';
  return name || type;
}

/** True when deleting this copy also removes it from an online account. */
export function isSyncedAccount(c: { accountType?: string }): boolean {
  return /google|exchange|outlook|yahoo/i.test(c.accountType || '');
}
